export type MessageStatus = "sent" | "delivered" | "read";

export type ChatMessageType = "text" | "system";

export interface ChatMessage {
  id: string;
  activity_id: string;
  sender_id: string;
  content: string;
  type: ChatMessageType;
  status: MessageStatus;
  delivered_at: string | null;
  read_at: string | null;
  created_at: string;
  /**
   * System-row payload written by the BE (gql chat PR #394): `event` (bare
   * lifecycle token) plus event data (`schedule.startAt/endAt`,
   * `price.previousTotal/newTotal/currency`). NULL on user rows and on
   * legacy purchase rows. Optional: RPC shapes and older cached rows may
   * omit the key entirely.
   */
  metadata?: unknown;
  /**
   * Superseded system event (e.g. an old reschedule proposal after a new
   * one). The BE flips it via a row UPDATE, delivered over Realtime.
   * Rendering hint only — action gating derives from the order status.
   */
  is_outdated?: boolean;
}

export interface ChatParticipant {
  id: string;
  activity_id: string;
  user_id: string;
  role: "professional" | "consumer";
  joined_at: string;
  last_read_at: string | null;
}

export interface SendMessageParams {
  activityId: string;
  senderId: string;
  content: string;
}

export interface FetchMessagesOptions {
  limit?: number;
  before?: string;
}

/**
 * One row per activity from the `chat_get_unread_counts` RPC.
 *
 * `unread_count` counts unread TEXT rows only (system rows are excluded),
 * while the `last_message_*` preview fields cover ALL rows — a system row is
 * routinely the latest message. Activities with zero unread text messages are
 * not returned at all (HAVING count > 0), even when their last message is an
 * unread system row.
 */
export interface UnreadCount {
  activity_id: string;
  unread_count: number;
  last_message_at: string;
  last_message_content: string;
  /**
   * NULL when the last message is a BE-written system row. Legacy purchase
   * system rows carry a REAL sender id, so "is this a system message?" must
   * key off `last_message_type`, never off a missing sender.
   */
  last_message_sender_id: string | null;
  last_message_type: ChatMessageType;
}

/** Supabase Realtime channel status values */
export type RealtimeStatus =
  "SUBSCRIBED" | "TIMED_OUT" | "CLOSED" | "CHANNEL_ERROR";

// ---------------------------------------------------------------------------
// Order-chat system-message tokens — THE single source (EAH-1931).
// ---------------------------------------------------------------------------

/**
 * Every activity lifecycle token the BE writes into `chat_messages.content`
 * for system rows (gql chat PR #394 + EAH-1885, the BE's
 * `emitActivityChatSystemMessage`). Content is `<token>[:<suffix>]` — the
 * suffix is an instance discriminator for the BE's idempotency index: the
 * event instant ISO on repeatable tokens, the actor role on
 * `activity_completed`.
 *
 * Single source of truth: the dictionaries copy helper
 * (`getChatSystemMessageText`), the ui-native card registry and the
 * utils-native timeline hook all derive from this list. Add new BE tokens
 * HERE — the card registry's exhaustive Record then breaks the build until
 * a card is chosen for the new token.
 */
export const KNOWN_ORDER_CHAT_TOKENS = [
  "activity_purchased",
  "reschedule_proposed",
  "appointment_updated",
  "activity_approved",
  "activity_scheduled",
  "activity_rejected",
  "activity_canceled",
  "activity_completed",
  "meeting_link_updated",
] as const;

export type KnownOrderChatToken = (typeof KNOWN_ORDER_CHAT_TOKENS)[number];

const KNOWN_TOKEN_SET: ReadonlySet<string> = new Set(KNOWN_ORDER_CHAT_TOKENS);

/**
 * Exact membership test — no suffix handling. For values that are bare
 * tokens by contract (`metadata.event`), or bases already split with
 * `splitOrderChatToken`.
 */
export const isKnownOrderChatToken = (
  value: string,
): value is KnownOrderChatToken => KNOWN_TOKEN_SET.has(value);

/**
 * Splits a `chat_messages.content` machine key into its base token and
 * optional instance suffix — everything after the FIRST `:` (ISO instants
 * contain `:` themselves). Content is a machine key: never parse dates or
 * params from it — dynamic data rides in `metadata`.
 */
export const splitOrderChatToken = (
  content: string,
): { baseToken: string; suffix?: string } => {
  const separatorIndex = content.indexOf(":");
  return separatorIndex === -1
    ? { baseToken: content }
    : {
        baseToken: content.slice(0, separatorIndex),
        suffix: content.slice(separatorIndex + 1),
      };
};

/**
 * Resolves raw content to its known base token (suffix stripped), or
 * undefined for tokens this app version does not know — callers fall back
 * to the generic system-message rendering.
 */
export const toKnownOrderChatToken = (
  token: string,
): KnownOrderChatToken | undefined => {
  const { baseToken } = splitOrderChatToken(token);
  return isKnownOrderChatToken(baseToken) ? baseToken : undefined;
};
